import { Injectable, signal, computed, Signal } from '@angular/core';

/**
 * Persists the in-flight filing session id across page navigations so a
 * partially-completed upload can be resumed on the next submit.
 *
 * Wraps `sessionStorage` behind a service boundary per CLAUDE.md's DIP rule —
 * components depend on this abstraction rather than reading storage directly.
 * Exposes the current id as a read-only signal for template/consumer use.
 */
@Injectable({ providedIn: 'root' })
export class FilingSessionStore {
  private static readonly STORAGE_KEY = 'filingSessionId';

  private readonly sessionIdSignal = signal<string | null>(
    this.readFromStorage()
  );

  readonly sessionId: Signal<string | null> = this.sessionIdSignal.asReadonly();
  readonly hasSession: Signal<boolean> = computed(
    () => this.sessionIdSignal() !== null
  );

  get(): string | null {
    return this.sessionIdSignal();
  }

  set(id: string): void {
    sessionStorage.setItem(FilingSessionStore.STORAGE_KEY, id);
    this.sessionIdSignal.set(id);
  }

  clear(): void {
    sessionStorage.removeItem(FilingSessionStore.STORAGE_KEY);
    this.sessionIdSignal.set(null);
  }

  private readFromStorage(): string | null {
    return sessionStorage.getItem(FilingSessionStore.STORAGE_KEY);
  }
}
